// Browser-safe helpers that embed a DPI (print resolution) into an encoded
// PNG or JPEG. Canvas exports carry no usable density metadata, so print-prep
// tools need to patch the encoded bytes directly: a pHYs chunk for PNG and a
// JFIF APP0 density block for JPEG. Verified against sharp's density reader.

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dpiToPixelsPerMeter(dpi: number) {
  return Math.round(dpi / 0.0254);
}

function readUint32BE(bytes: Uint8Array, offset: number) {
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  );
}

function concat(parts: Uint8Array[]) {
  const size = parts.reduce((total, part) => total + part.length, 0);
  const merged = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    merged.set(part, offset);
    offset += part.length;
  }
  return merged;
}

function isPng(bytes: Uint8Array) {
  if (bytes.length < 8) {
    return false;
  }
  for (let i = 0; i < 8; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) {
      return false;
    }
  }
  return true;
}

/** Insert (or replace) a pHYs chunk so the PNG reports the given DPI. */
export function setPngDpi(bytes: Uint8Array, dpi: number): Uint8Array {
  if (!isPng(bytes)) {
    throw new Error('Not a PNG file.');
  }

  const ppm = dpiToPixelsPerMeter(dpi);
  const type = new Uint8Array([0x70, 0x48, 0x59, 0x73]); // "pHYs"
  const data = new Uint8Array(9);
  data[0] = (ppm >>> 24) & 0xff;
  data[1] = (ppm >>> 16) & 0xff;
  data[2] = (ppm >>> 8) & 0xff;
  data[3] = ppm & 0xff;
  data[4] = data[0];
  data[5] = data[1];
  data[6] = data[2];
  data[7] = data[3];
  data[8] = 1; // unit specifier: meter

  const chunk = new Uint8Array(12 + 9);
  chunk[0] = 0;
  chunk[1] = 0;
  chunk[2] = 0;
  chunk[3] = 9;
  chunk.set(type, 4);
  chunk.set(data, 8);
  const crc = crc32(concat([type, data]));
  chunk[17] = (crc >>> 24) & 0xff;
  chunk[18] = (crc >>> 16) & 0xff;
  chunk[19] = (crc >>> 8) & 0xff;
  chunk[20] = crc & 0xff;

  const parts: Uint8Array[] = [bytes.slice(0, 8)];
  let pos = 8;
  let inserted = false;

  while (pos + 8 <= bytes.length) {
    const length = readUint32BE(bytes, pos);
    const chunkType = String.fromCharCode(
      bytes[pos + 4],
      bytes[pos + 5],
      bytes[pos + 6],
      bytes[pos + 7],
    );
    const total = 12 + length;

    if (chunkType === 'pHYs') {
      pos += total; // drop any existing density chunk
      continue;
    }

    if (!inserted && (chunkType === 'IDAT' || chunkType === 'IEND')) {
      parts.push(chunk);
      inserted = true;
    }

    parts.push(bytes.slice(pos, pos + total));
    pos += total;
  }

  if (!inserted) {
    parts.push(chunk);
  }

  return concat(parts);
}

/** Replace the JFIF APP0 density block so the JPEG reports the given DPI. */
export function setJpegDpi(bytes: Uint8Array, dpi: number): Uint8Array {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    throw new Error('Not a JPEG file.');
  }

  const density = Math.max(1, Math.min(0xffff, Math.round(dpi)));
  const app0 = new Uint8Array([
    0xff,
    0xe0,
    0x00,
    0x10,
    0x4a,
    0x46,
    0x49,
    0x46,
    0x00, // "JFIF\0"
    0x01,
    0x02, // version 1.02
    0x01, // units: dots per inch
    (density >> 8) & 0xff,
    density & 0xff, // X density
    (density >> 8) & 0xff,
    density & 0xff, // Y density
    0x00,
    0x00, // no thumbnail
  ]);

  // Drop an existing JFIF APP0 that immediately follows SOI, if present.
  let rest = bytes.slice(2);
  if (
    rest.length > 8 &&
    rest[0] === 0xff &&
    rest[1] === 0xe0 &&
    rest[4] === 0x4a &&
    rest[5] === 0x46 &&
    rest[6] === 0x49 &&
    rest[7] === 0x46
  ) {
    const length = (rest[2] << 8) | rest[3];
    rest = rest.slice(2 + length);
  }

  return concat([bytes.slice(0, 2), app0, rest]);
}

export type DpiImageFormat = 'png' | 'jpeg';

/** Embed a DPI value into an encoded PNG or JPEG blob and return a new blob. */
export async function embedImageDpi(
  blob: Blob,
  dpi: number,
  format: DpiImageFormat,
): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const patched = format === 'png' ? setPngDpi(bytes, dpi) : setJpegDpi(bytes, dpi);
  // Copy into a fresh ArrayBuffer so the Blob gets a clean, exact-length buffer.
  const buffer = patched.slice().buffer;
  return new Blob([buffer], { type: format === 'png' ? 'image/png' : 'image/jpeg' });
}
